import {
  ComponentType,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

type Environment = 'local' | 'dev' | 'prod';

/**
 * Uses import.meta.env variables (NODE_ENV, VITE_FIREBASE_EMULATOR)
 * @returns {'local' | 'dev' | 'prod'}
 */
const getCurrentEnv = (): Environment => {
  let env: Environment;
  if (import.meta.env.MODE === 'development') {
    env = 'dev';
    if (import.meta.env.VITE_FIREBASE_EMULATOR === 'ACTIVE') {
      env = 'local';
    }
  } else {
    env = 'prod';
  }
  return env;
};

/**
 * @param {'local' | 'dev' | 'prod'} env
 * @returns {boolean}
 */
const isLocal = (env: Environment = getCurrentEnv()): boolean =>
  env === 'local';

/**
 * @param {'local' | 'dev' | 'prod'} env
 * @returns {boolean}
 */
const isDev = (env: Environment = getCurrentEnv()): boolean => env === 'dev';

/**
 * @param {'local' | 'dev' | 'prod'} env
 * @returns {boolean}
 */
const isProd = (env: Environment = getCurrentEnv()): boolean => env === 'prod';

interface EnvContextType {
  env: Environment;
  setEnv: Dispatch<SetStateAction<Environment>>;
  isLocal: () => boolean;
  isDev: () => boolean;
  isProd: () => boolean;
}

const EnvContext = createContext<EnvContextType>({
  env: 'local',
  setEnv: () => {},
  isLocal: () => false,
  isDev: () => false,
  isProd: () => false,
});

interface EnvProviderProps {
  children: ReactNode;
}

/**
 * @returns {React.Provider}
 */
const EnvProvider = ({ children }: EnvProviderProps) => {
  const [env, setEnv] = useState<Environment>(getCurrentEnv());
  return (
    <EnvContext.Provider
      value={{
        env,
        setEnv,
        isLocal: () => isLocal(env),
        isDev: () => isDev(env),
        isProd: () => isProd(env),
      }}
    >
      {children}
    </EnvContext.Provider>
  );
};

/**
 * Makes the Env context available with the useEnv hook
 * @param {React.Component} WrappedComponent
 * @returns {React.Component}
 */
const withEnv = <P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>
) =>
  function WrappedWithEnv(props: P) {
    return (
      <EnvProvider>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <WrappedComponent {...(props as any)} />
      </EnvProvider>
    );
  };

/**
 * returns the EnvContext object
 */
const useEnv = (): EnvContextType => useContext(EnvContext);

export { withEnv, useEnv, getCurrentEnv, isLocal, isDev, isProd };
export type { Environment };
